import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import api from '../utils/api';
import { useBranch } from '../contexts/BranchContext';
import { imgUrl } from '../utils/imgUrl';
import { VoiceInput } from '../components/VoiceInput';

const Counselling = () => {
  const { activeBranch } = useBranch();
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [aiGuide, setAiGuide] = useState('');
  const [generatingGuide, setGeneratingGuide] = useState(false);
  const [counsellingNote, setCounsellingNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [attendanceData, setAttendanceData] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [reportsData, setReportsData] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [loadingCallHistory, setLoadingCallHistory] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setCustomerData(null);
    setAiGuide('');
    setCounsellingNote('');
    try {
      // Pass branch_id for proper branch isolation
      const branchParam = activeBranch?.branch_id ? `?branch_id=${activeBranch.branch_id}` : '';
      const res = await api.get(`/counselling/customer/${encodeURIComponent(searchQuery.trim())}${branchParam}`);
      setCustomerData(res.data);
      toast.success(`Found: ${res.data.customer?.name || 'Customer'}`);
      // Auto-generate AI guide and fetch call history
      const customerId = res.data.customer_id || res.data.customer?.customer_id;
      generateAIGuide(customerId);
      fetchCallHistory(customerId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Customer not found');
    } finally {
      setSearching(false);
    }
  }, [searchQuery, activeBranch]);

  const generateAIGuide = async (customerId) => {
    if (!customerId) return;
    setGeneratingGuide(true);
    try {
      const res = await api.post(`/counselling/ai-guide/${customerId}`, { language: 'english' });
      setAiGuide(res.data.guide);
      if (res.data.credits_used > 0) {
        toast.success(`AI Guide generated (${res.data.credits_used} credit used, ${res.data.credits_remaining} remaining)`);
      } else {
        toast.success('AI Guide generated');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to generate guide';
      if (err.response?.status === 402) {
        toast.error('Insufficient AI credits. Please recharge.');
      } else {
        toast.error(detail);
      }
      setAiGuide('');
    } finally {
      setGeneratingGuide(false);
    }
  };

  const handleSaveNote = async () => {
    if (!counsellingNote.trim() || !customerData) return;
    setSavingNote(true);
    try {
      const customerId = customerData.customer_id || customerData.customer?.customer_id;
      await api.post(`/counselling/notes/${customerId}`, {
        notes: counsellingNote,
        customer_name: customerData.customer?.name || ''
      });
      toast.success('Counselling note saved');
      setCounsellingNote('');
      // Refresh counselling notes
      const res = await api.get(`/counselling/notes/${customerId}`);
      setCustomerData(prev => ({ ...prev, counselling_notes: res.data.notes }));
    } catch (err) {
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const fetchAttendance = async (customerId) => {
    if (!customerId) return;
    setLoadingAttendance(true);
    try {
      const res = await api.get(`/counselling/attendance/${customerId}`);
      setAttendanceData(res.data.attendance || []);
    } catch (err) {
      toast.error('Failed to load attendance data');
      setAttendanceData([]);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const fetchReports = async (customerId) => {
    if (!customerId) return;
    setLoadingReports(true);
    try {
      const res = await api.get(`/counselling/reports/${customerId}`);
      setReportsData(res.data);
    } catch (err) {
      toast.error('Failed to load reports');
      setReportsData(null);
    } finally {
      setLoadingReports(false);
    }
  };

  const handlePhotoUpload = async (event, reportType = 'session') => {
    const files = event.target.files;
    if (!files || files.length === 0 || !customerData) return;
    
    const customerId = customerData.customer_id || customerData.customer?.customer_id;
    setUploadingPhoto(true);
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      // Upload each file separately
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('report_type', reportType);
          
          await api.post(`/counselling/reports/${customerId}/upload-photo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          successCount++;
        } catch (err) {
          failCount++;
          console.error(`Failed to upload ${file.name}:`, err);
        }
      }
      
      // Show appropriate toast message
      if (successCount > 0 && failCount === 0) {
        toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded successfully`);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded, ${failCount} failed`);
      } else {
        toast.error('Failed to upload photos');
      }
      
      // Refresh reports
      fetchReports(customerId);
      
      // Clear the input
      event.target.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm(`Delete this session photo from ${photo.date}? This cannot be undone.`)) return;
    const customerId = customerData.customer_id || customerData.customer?.customer_id;
    try {
      await api.delete(`/counselling/reports/${customerId}/photo/${photo.note_id}`);
      toast.success('Photo deleted');
      fetchReports(customerId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete photo');
    }
  };

  const fetchCallHistory = async (customerId) => {
    if (!customerId) return;
    setLoadingCallHistory(true);
    try {
      const res = await api.get(`/counselling/call-history/${customerId}`);
      setCallHistory(res.data.call_history || []);
    } catch (err) {
      toast.error('Failed to load call history');
      setCallHistory([]);
    } finally {
      setLoadingCallHistory(false);
    }
  };

  const customer = customerData?.customer;
  const stats = customerData?.stats;
  const sessionNotes = customerData?.session_notes || [];
  const counsellingNotes = customerData?.counselling_notes || [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-[#2C4C3B]">🩺 Counselling</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Search a customer to view their complete profile and get AI-powered counselling guidance
            {activeBranch && <span className="ml-2 font-medium text-[#2C4C3B]">• Branch: {activeBranch.name}</span>}
          </p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm border p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter Phone Number or Registration Number..."
                className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent text-sm"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searching || !searchQuery.trim()}
              className="px-6 py-2.5 sm:py-3 bg-[#2C4C3B] text-white rounded-lg hover:bg-[#1a3a2a] disabled:opacity-50 text-sm font-medium whitespace-nowrap"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {/* No customer yet */}
        {!customerData && !searching && (
          <div className="bg-white rounded-xl shadow-sm border p-8 sm:p-12 text-center">
            <div className="text-4xl sm:text-5xl mb-4">🩺</div>
            <h3 className="text-base sm:text-lg font-medium text-gray-600">Search for a customer to begin counselling</h3>
            <p className="text-xs sm:text-sm text-gray-400 mt-2">Enter their phone number or registration number above</p>
          </div>
        )}

        {/* Customer Found - Full Profile */}
        {customerData && customer && (
          <div className="space-y-4 sm:space-y-6">
            {/* Customer Info Card */}
            <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-[#2C4C3B] text-white flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0">
                  {(customer.name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="font-semibold text-sm sm:text-base text-[#2C4C3B]">{customer.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="font-medium text-sm">{customer.phone_number || customer.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Age</p>
                    <p className="font-medium text-sm">{customer.age || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Reg No</p>
                    <p className="font-medium text-sm">{customer.registration_number || customer.reg_no || 'N/A'}</p>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <p className="text-xs text-gray-500">Health Concern</p>
                    <p className="font-medium text-sm">{customer.problem || customer.health_concern || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Category</p>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      stats?.category === 'Buyer' ? 'bg-green-100 text-green-800' :
                      stats?.category === '30 Days' ? 'bg-blue-100 text-blue-800' :
                      stats?.category === '10 Days' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {stats?.category || 'New'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Sessions</p>
                    <p className="font-medium text-sm">{stats?.total_sessions || 0} / {stats?.planned_sessions || 30}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Attendance</p>
                    <p className="font-medium text-sm">{stats?.attendance_rate || 0}%</p>
                  </div>
                </div>
              </div>
              <div className="mt-3 sm:mt-4 flex flex-wrap gap-2 sm:gap-4 text-xs text-gray-500">
                <span>First Visit: {stats?.first_visit || 'N/A'}</span>
                <span>Last Visit: {stats?.last_visit || 'N/A'}</span>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto">
              {[
                { key: 'overview', label: '📊 AI Guide' },
                { key: 'sessions', label: '📋 Sessions' },
                { key: 'notes', label: '📝 Notes' },
                { key: 'pain', label: '📈 Pain Trend' },
                { key: 'attendance', label: '👥 Attendance' },
                { key: 'reports', label: '📸 Reports' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    const customerId = customerData?.customer_id || customerData?.customer?.customer_id;
                    if (tab.key === 'attendance' && customerId) fetchAttendance(customerId);
                    if (tab.key === 'reports' && customerId) fetchReports(customerId);
                  }}
                  className={`flex-1 min-w-[90px] px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                    activeTab === tab.key
                      ? 'bg-white text-[#2C4C3B] shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* AI Guide Tab */}
            {activeTab === 'overview' && (
              <div className="bg-gradient-to-br from-[#f0f7f4] to-white rounded-xl shadow-sm border p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-[#2C4C3B]">🤖 AI Counselling Guide</h3>
                  <button
                    onClick={() => generateAIGuide(customerData.customer_id || customer.customer_id)}
                    disabled={generatingGuide}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#2C4C3B] text-white rounded-lg hover:bg-[#1a3a2a] disabled:opacity-50 text-xs font-medium"
                  >
                    {generatingGuide ? '⏳ Generating...' : '🔄 Regenerate'}
                  </button>
                </div>
                {generatingGuide && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C4C3B] mr-3"></div>
                    <span className="text-sm text-gray-500">Generating AI counselling guide...</span>
                  </div>
                )}
                {aiGuide && !generatingGuide && (
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed text-sm">
                    {aiGuide}
                  </div>
                )}
                {!aiGuide && !generatingGuide && (
                  <p className="text-gray-400 text-xs sm:text-sm py-8 text-center">AI guide will auto-generate when you search a customer. Click "Regenerate" to try again.</p>
                )}
              </div>
            )}

            {/* Sessions Tab */}
            {activeTab === 'sessions' && (
              <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-[#2C4C3B] mb-4">📋 Session History ({sessionNotes.length} sessions)</h3>
                {sessionNotes.length === 0 ? (
                  <p className="text-gray-400 text-center py-8 text-sm">No session records found</p>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {sessionNotes.map((sn, i) => (
                      <div key={i} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-1 flex-wrap gap-2">
                          <span className="text-xs sm:text-sm font-medium text-[#2C4C3B]">{sn.date || 'Unknown date'}</span>
                          <div className="flex gap-2 flex-wrap">
                            {sn.pain_score != null && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                Number(sn.pain_score) <= 3 ? 'bg-green-100 text-green-700' :
                                Number(sn.pain_score) <= 6 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>Pain: {sn.pain_score}/10</span>
                            )}
                            {sn.effectiveness && (
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">{sn.effectiveness}</span>
                            )}
                          </div>
                        </div>
                        {(sn.therapy_name || sn.therapy) && (
                          <p className="text-xs text-gray-500">Therapy: {sn.therapy_name || sn.therapy}</p>
                        )}
                        {(sn.notes || sn.session_notes) && (
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">{sn.notes || sn.session_notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div className="space-y-4">
                {/* Add new note */}
                <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base sm:text-lg font-semibold text-[#2C4C3B]">📝 Today's Counselling Notes</h3>
                    <VoiceInput onText={(t) => setCounsellingNote((prev) => (prev ? prev + ' ' : '') + t)} />
                  </div>
                  <textarea
                    data-testid="counselling-notes-textarea"
                    value={counsellingNote}
                    onChange={(e) => setCounsellingNote(e.target.value)}
                    placeholder="Write your counselling notes here... What was discussed? What did the customer say? Next actions?"
                    rows={4}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent text-sm resize-none"
                  />
                  <button
                    onClick={handleSaveNote}
                    disabled={savingNote || !counsellingNote.trim()}
                    className="mt-3 px-4 sm:px-6 py-2 sm:py-2.5 bg-[#2C4C3B] text-white rounded-lg hover:bg-[#1a3a2a] disabled:opacity-50 text-sm font-medium"
                  >
                    {savingNote ? 'Saving...' : '💾 Save Notes'}
                  </button>
                </div>

                {/* Previous notes */}
                <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-[#2C4C3B] mb-3">Previous Counselling Notes ({counsellingNotes.length})</h3>
                  {counsellingNotes.length === 0 ? (
                    <p className="text-gray-400 text-center py-6 text-sm">No previous counselling notes</p>
                  ) : (
                    <div className="space-y-3">
                      {counsellingNotes.map((cn, i) => (
                        <div key={i} className="border-l-4 border-[#2C4C3B] pl-3 sm:pl-4 py-2">
                          <div className="flex justify-between items-center mb-1 flex-wrap gap-1">
                            <span className="text-xs sm:text-sm font-medium text-[#2C4C3B]">{cn.date}</span>
                            <span className="text-xs text-gray-400">by {cn.created_by_name || cn.created_by}</span>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-700">{cn.notes}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Call History Card - Always visible */}
            {customerData && customer && (
              <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6 mt-4">
                <h3 className="text-base sm:text-lg font-semibold text-[#2C4C3B] mb-4">📞 Call History ({callHistory.length})</h3>
                {loadingCallHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2C4C3B]"></div>
                  </div>
                ) : callHistory.length === 0 ? (
                  <p className="text-gray-400 text-center py-6 text-sm">No call history found</p>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {callHistory.map((call, i) => (
                      <div key={i} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs sm:text-sm font-medium text-[#2C4C3B]">
                              {new Date(call.date || call.created_at).toLocaleDateString('en-IN')}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              call.call_type === 'ai_call' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {call.call_type === 'ai_call' ? '🤖 AI Call' : '📞 Manual Call'}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              call.status === 'completed' ? 'bg-green-100 text-green-700' :
                              call.status === 'no_answer' ? 'bg-yellow-100 text-yellow-700' :
                              call.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {call.status}
                            </span>
                            {call.created_by_name && (
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                                👤 {call.created_by_name}
                              </span>
                            )}
                          </div>
                          {call.duration_seconds > 0 && (
                            <span className="text-xs text-gray-500">
                              {Math.floor(call.duration_seconds / 60)}:{(call.duration_seconds % 60).toString().padStart(2, '0')} min
                            </span>
                          )}
                        </div>
                        
                        {call.call_summary && (
                          <div className="mt-2 bg-gray-50 rounded p-2">
                            <p className="text-xs font-medium text-gray-600 mb-1">Call Summary:</p>
                            <p className="text-xs sm:text-sm text-gray-700">{call.call_summary}</p>
                          </div>
                        )}
                        
                        {call.transcript && (
                          <details className="mt-2">
                            <summary className="text-xs text-blue-600 cursor-pointer hover:underline">View Transcript</summary>
                            <p className="text-xs text-gray-600 mt-1 pl-3 border-l-2 border-gray-200">{call.transcript}</p>
                          </details>
                        )}
                        
                        {call.call_recording_url && (
                          <div className="mt-2">
                            <a 
                              href={call.call_recording_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                              🎧 Listen to Recording
                            </a>
                          </div>
                        )}
                        
                        <div className="mt-2 flex justify-between items-center text-xs text-gray-400">
                          <span>
                            {new Date(call.created_at).toLocaleString('en-IN', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              day: '2-digit',
                              month: 'short'
                            })}
                          </span>
                          {call.credits_used > 0 && (
                            <span className="text-xs text-purple-600">💳 {call.credits_used} credits</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pain Trend Tab */}
            {activeTab === 'pain' && (
              <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-[#2C4C3B] mb-4">📈 Pain Score Trend</h3>
                {stats?.pain_scores?.length > 0 ? (
                  <div>
                    <div className="flex items-end gap-1 h-32 sm:h-48 border-b border-l border-gray-200 p-2 sm:p-4">
                      {stats.pain_scores.map((ps, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
                          <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                            {ps.date}: {ps.score}/10
                          </div>
                          <div
                            className={`w-full max-w-[30px] rounded-t transition-all ${
                              ps.score <= 3 ? 'bg-green-400' : ps.score <= 6 ? 'bg-yellow-400' : 'bg-red-400'
                            }`}
                            style={{ height: `${(ps.score / 10) * 100}%` }}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-2 px-2 sm:px-4">
                      <span>{stats.pain_scores[0]?.date}</span>
                      <span>{stats.pain_scores[stats.pain_scores.length - 1]?.date}</span>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-4 text-center">
                      <div className="bg-green-50 rounded-lg p-2 sm:p-3">
                        <p className="text-xs text-gray-500">First Score</p>
                        <p className="text-base sm:text-lg font-bold text-green-700">{stats.pain_scores[0]?.score}/10</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-2 sm:p-3">
                        <p className="text-xs text-gray-500">Latest Score</p>
                        <p className="text-base sm:text-lg font-bold text-blue-700">{stats.pain_scores[stats.pain_scores.length - 1]?.score}/10</p>
                      </div>
                      <div className={`rounded-lg p-2 sm:p-3 ${
                        stats.pain_scores[stats.pain_scores.length - 1]?.score < stats.pain_scores[0]?.score ? 'bg-green-50' : 'bg-red-50'
                      }`}>
                        <p className="text-xs text-gray-500">Change</p>
                        <p className={`text-base sm:text-lg font-bold ${
                          stats.pain_scores[stats.pain_scores.length - 1]?.score < stats.pain_scores[0]?.score ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {stats.pain_scores[stats.pain_scores.length - 1]?.score - stats.pain_scores[0]?.score > 0 ? '+' : ''}
                          {(stats.pain_scores[stats.pain_scores.length - 1]?.score - stats.pain_scores[0]?.score).toFixed(1)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-8 text-sm">No pain score data available</p>
                )}
              </div>
            )}

            {/* Attendance Tab */}
            {activeTab === 'attendance' && (
              <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-[#2C4C3B] mb-4">👥 Attendance History</h3>
                {loadingAttendance ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C4C3B]"></div>
                  </div>
                ) : attendanceData.length === 0 ? (
                  <p className="text-gray-400 text-center py-8 text-sm">No attendance records found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Date</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Staff</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Check-in</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Check-out</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Therapy</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Pain Score</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {attendanceData.map((record, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">{record.date || 'N/A'}</td>
                            <td className="px-3 py-2">{record.staff_name || record.created_by_name || '-'}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {record.check_in_time ? new Date(record.check_in_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {record.check_out_time ? new Date(record.check_out_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                            <td className="px-3 py-2 text-xs">{record.therapy_type || '-'}</td>
                            <td className="px-3 py-2">
                              {record.pain_score != null ? (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  record.pain_score <= 3 ? 'bg-green-100 text-green-700' :
                                  record.pain_score <= 6 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {record.pain_score}/10
                                </span>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Reports Tab */}
            {activeTab === 'reports' && (
              <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-[#2C4C3B]">📸 Reports & Photos</h3>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <label data-testid="upload-before-report" className="px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 cursor-pointer text-xs font-medium inline-flex items-center">
                      <input type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'before')} className="hidden" disabled={uploadingPhoto} />
                      Before Report
                    </label>
                    <label data-testid="upload-after-report" className="px-3 py-1.5 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 cursor-pointer text-xs font-medium inline-flex items-center">
                      <input type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'after')} className="hidden" disabled={uploadingPhoto} />
                      After Report
                    </label>
                    <label data-testid="upload-session-photos" className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#2C4C3B] text-white rounded-lg hover:bg-[#1a3a2a] cursor-pointer text-xs font-medium inline-flex items-center">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePhotoUpload(e, 'session')}
                        className="hidden"
                        disabled={uploadingPhoto}
                      />
                      {uploadingPhoto ? '⏳ Uploading...' : '📷 Upload Photos'}
                    </label>
                  </div>
                </div>

                {loadingReports ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C4C3B]"></div>
                  </div>
                ) : !reportsData ? (
                  <p className="text-gray-400 text-center py-8 text-sm">No reports available</p>
                ) : (
                  <div className="space-y-6">
                    {/* Before / After Compare */}
                    {reportsData.clinical_reports?.before_report && reportsData.clinical_reports?.after_report && (
                      <div data-testid="before-after-compare" className="rounded-xl border border-[#E5E5DF] bg-[#F7F7F2] p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-[#2C4C3B]">Before vs After</h4>
                          <button
                            data-testid="compare-fullscreen-btn"
                            onClick={() => { setSelectedImage({ compare: true, before: reportsData.clinical_reports.before_report, after: reportsData.clinical_reports.after_report, label: 'Before vs After' }); setImageModalOpen(true); }}
                            className="text-xs font-medium text-[#2C4C3B] underline hover:text-[#C05C42]"
                          >
                            View large
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <span className="inline-block text-[10px] uppercase tracking-wider font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded mb-2">Before</span>
                            <img src={imgUrl(reportsData.clinical_reports.before_report)} alt="Before" className="w-full h-48 sm:h-64 object-contain bg-white rounded-lg border border-blue-200" />
                          </div>
                          <div>
                            <span className="inline-block text-[10px] uppercase tracking-wider font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded mb-2">After</span>
                            <img src={imgUrl(reportsData.clinical_reports.after_report)} alt="After" className="w-full h-48 sm:h-64 object-contain bg-white rounded-lg border border-green-200" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Clinical Reports Section */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Clinical Reports</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {reportsData.clinical_reports?.before_report && (
                          <div 
                            className="relative group cursor-pointer"
                            onClick={() => {
                              setSelectedImage({ url: reportsData.clinical_reports.before_report, label: 'Before Report' });
                              setImageModalOpen(true);
                            }}
                          >
                            <img
                              src={imgUrl(reportsData.clinical_reports.before_report)}
                              alt="Before Report"
                              className="w-full h-32 object-cover rounded-lg border-2 border-blue-200"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <span className="text-white text-xs font-medium">🔍 Click to View</span>
                            </div>
                          </div>
                        )}
                        {reportsData.clinical_reports?.after_report && (
                          <div 
                            className="relative group cursor-pointer"
                            onClick={() => {
                              setSelectedImage({ url: reportsData.clinical_reports.after_report, label: 'After Report' });
                              setImageModalOpen(true);
                            }}
                          >
                            <img
                              src={imgUrl(reportsData.clinical_reports.after_report)}
                              alt="After Report"
                              className="w-full h-32 object-cover rounded-lg border-2 border-green-200"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <span className="text-white text-xs font-medium">🔍 Click to View</span>
                            </div>
                          </div>
                        )}
                        {reportsData.clinical_reports?.customer_photo && (
                          <div 
                            className="relative group cursor-pointer"
                            onClick={() => {
                              setSelectedImage({ url: reportsData.clinical_reports.customer_photo, label: 'Profile Photo' });
                              setImageModalOpen(true);
                            }}
                          >
                            <img
                              src={imgUrl(reportsData.clinical_reports.customer_photo)}
                              alt="Profile Photo"
                              className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <span className="text-white text-xs font-medium">🔍 Click to View</span>
                            </div>
                          </div>
                        )}
                      </div>
                      {!reportsData.clinical_reports?.before_report && !reportsData.clinical_reports?.after_report && !reportsData.clinical_reports?.customer_photo && (
                        <p className="text-gray-400 text-sm text-center py-4">No clinical reports uploaded</p>
                      )}
                    </div>

                    {/* Session Photos Section */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Session Photos ({reportsData.total_photos || 0})
                      </h4>
                      {reportsData.session_photos?.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                          {reportsData.session_photos.map((photo, i) => (
                            <div 
                              key={i} 
                              className="relative group cursor-pointer rounded-lg overflow-hidden"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedImage({ url: photo.image_url, label: `Session ${photo.date}`, uploadedBy: photo.uploaded_by });
                                setImageModalOpen(true);
                              }}
                            >
                              <img
                                src={imgUrl(photo.image_url)}
                                alt={`Session ${photo.date}`}
                                className="w-full h-32 object-cover border"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2">
                                <span className="text-white text-xs font-medium">🔍 Click to View</span>
                                <span className="text-gray-300 text-xs mt-1">{photo.date}</span>
                                {photo.uploaded_by && (
                                  <span className="text-gray-300 text-xs">by {photo.uploaded_by}</span>
                                )}
                              </div>
                              <button
                                data-testid={`delete-session-photo-${i}`}
                                title="Delete photo"
                                onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo); }}
                                className="absolute top-1.5 right-1.5 z-10 bg-white/90 text-red-600 hover:bg-red-600 hover:text-white rounded-full w-7 h-7 flex items-center justify-center shadow transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-400 text-sm text-center py-4">No session photos uploaded</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Image Modal */}
        {imageModalOpen && selectedImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
            onClick={() => setImageModalOpen(false)}
          >
            <div className="relative max-w-4xl max-h-[90vh] w-full">
              <button
                onClick={() => setImageModalOpen(false)}
                className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="bg-white rounded-lg overflow-hidden">
                {selectedImage.compare ? (
                  <div className="grid grid-cols-2 gap-2 p-2 bg-gray-100" onClick={(e) => e.stopPropagation()}>
                    <div className="text-center">
                      <span className="text-xs font-semibold text-blue-700">BEFORE</span>
                      <img src={imgUrl(selectedImage.before)} alt="Before" className="w-full max-h-[70vh] object-contain bg-white rounded" />
                    </div>
                    <div className="text-center">
                      <span className="text-xs font-semibold text-green-700">AFTER</span>
                      <img src={imgUrl(selectedImage.after)} alt="After" className="w-full max-h-[70vh] object-contain bg-white rounded" />
                    </div>
                  </div>
                ) : (
                <img
                  src={imgUrl(selectedImage.url)}
                  alt={selectedImage.label}
                  className="w-full h-auto max-h-[80vh] object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
                )}
                <div className="p-4 bg-gray-50">
                  <p className="font-semibold text-gray-800">{selectedImage.label}</p>
                  {selectedImage.uploadedBy && (
                    <p className="text-sm text-gray-600 mt-1">Uploaded by: {selectedImage.uploadedBy}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Counselling;
